import React, { ReactElement } from 'react';
import { SvgIcon } from '@material-ui/core';
import { CancelRounded } from '@material-ui/icons';
import BasicModal from '../../../modules/modals/BasicModal';
import JoinActionButton from '../JoinActionButton';
import JoinSkipButton from '../JoinSkipButton';
import { DashlineSeparator, FlexContainer, VerticalSeparator } from '../styledElements';
import VerifiedIcon from '../../../atoms/icons/VerifiedIcon';
import colors from '../../../theme/colors';
import {
  Headline,
  HeadlineContainer,
  HelpContainer,
  NotesSubtitle,
  NotesTitle,
  StepDescription,
  StyledContainer,
  selfieWallContentStyle,
  selfieWallModalStyle,
} from './SelfieVerificationStyle';
import { CloseBtn, closeButton } from '../../common/CommonStyle';
import useMediaBreakpoints from '../../../hooks/useMediaBreakpoints';
import { isDarkMode } from '../../../utils/helpers';
import SelfieIcon from 'atoms/icons/SelfieIcon';

interface LetsVerifyStepProps {
  showVerifyModal: boolean;
  handleContinueClick: () => void;
  showSkippable: boolean;
  handleSkipButton: () => void;
  isJoinFlow: boolean;
  handleClose: () => void;
}
const LetsVerifyStep: React.FC<LetsVerifyStepProps> = ({
  showVerifyModal,
  handleContinueClick,
  showSkippable,
  handleSkipButton,
  isJoinFlow,
  handleClose,
}): ReactElement => {
  const { isMobile } = useMediaBreakpoints();

  const renderInnerContent = () => {
    return (
      <>
        <HeadlineContainer isJoinFlow={isJoinFlow}>
          <SelfieIcon />
          <Headline>Let’s verify with a selfie</Headline>
          <StepDescription style={{ marginBottom: 0 }} isJoinFlow={isJoinFlow}>
            Take a selfie and let us know you’re real. Your selfie is secure and it won’t display on your profile.
          </StepDescription>
          <StepDescription style={{ color: colors.light.red.primary, fontSize: '13px' }} isJoinFlow={isJoinFlow}>
            For verification, you must have at least one photo showing your face (can be set to private) in your photo
            gallery.
          </StepDescription>
        </HeadlineContainer>
        <StyledContainer>
          <HelpContainer>
            <div style={{ marginRight: 8 }}>
              <VerifiedIcon fillColor={isJoinFlow || !isDarkMode() ? '#2B8FD7' : '#71B5E4'} />
            </div>
            <div>
              <NotesTitle isJoinFlow={isJoinFlow}>Help keep WYP Secure</NotesTitle>
              <NotesSubtitle isJoinFlow={isJoinFlow}>
                Create a safer environment for members to interact and meet offline by proving you are the person in
                your profile pics
              </NotesSubtitle>
            </div>
          </HelpContainer>
          {isJoinFlow && <DashlineSeparator style={{ marginTop: 20, marginBottom: 0 }} />}

          <div style={{ textAlign: 'center' }}>
            <JoinActionButton
              label={isJoinFlow ? 'Continue' : 'Next'}
              isDefault={true}
              onClick={handleContinueClick}
              customStyle={{ marginBottom: 20 }}
            />

            <FlexContainer>
              <VerticalSeparator>
                {showSkippable && (
                  <>
                    <JoinSkipButton
                      label="Skip for now"
                      onClick={handleSkipButton}
                      customStyle={{ color: colors.gray['1100'] }}
                    />
                  </>
                )}
              </VerticalSeparator>
            </FlexContainer>
          </div>
        </StyledContainer>
      </>
    );
  };

  return isJoinFlow ? (
    <>{renderInnerContent()}</>
  ) : (
    <>
      <BasicModal
        isOpen={showVerifyModal}
        hideClose={true}
        customStyle={selfieWallModalStyle}
        customContentStyle={selfieWallContentStyle}
        onClose={handleClose}
        shouldCloseOnOverlayClick={false}
        shouldCloseOnEsc={false}
        className="lets-verify-modal-content"
      >
        {showSkippable && (
          <CloseBtn
            data-test-id={`confirm-upload-close`}
            onClick={handleClose}
            style={{
              top: isMobile ? '-30px' : '-25px',
              right: isMobile ? '-18px' : '-35px',
              padding: 0,
              width: 24,
              height: 24,
            }}
          >
            <SvgIcon component={CancelRounded} style={closeButton} />
          </CloseBtn>
        )}
        {renderInnerContent()}
      </BasicModal>
    </>
  );
};

export default LetsVerifyStep;
